import React, { useState, useEffect, useCallback, memo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api } from '../api';
import Select from '../components/Select';
import InsumoAutocomplete from '../components/InsumoAutocomplete';
import Field, { FormRow } from '../components/ui/Field';
import DataTable from '../components/ui/DataTable';
import EmptyState from '../components/ui/EmptyState';
import Modal from '../components/ui/Modal';
import PageHeader from '../components/ui/PageHeader';
import Icon from '../components/ui/Icon';
import { useConfirm } from '../hooks/useConfirm';
import { toast } from '../components/Toast';
import { fmt, fmtBRL, fmtData, hoje } from '../utils/format';
import { CATEGORIAS } from '../utils/categorias';

const UNIDADES = ['KG', 'LITRO', 'UNIDADE', 'SACO', 'TONELADA', 'HORA', 'DIA', 'DIARIA'];

const FORM_VAZIO = {
  categoria: '', insumoNome: '', insumoId: null, unidadeMedida: '',
  isNovoInsumo: false, dataCompra: hoje(), quantidade: '', custoUnitario: '', fornecedor: '', adquiridoEm: '',
};

const HistoricoInsumo = memo(function HistoricoInsumo({ insumo }) {
  const [historico, setHistorico] = useState([]);
  const [carregando, setCarregando] = useState(true);

  useEffect(() => {
    setCarregando(true);
    api.getHistoricoPrecos(insumo.id)
      .then(setHistorico)
      .catch(() => setHistorico([]))
      .finally(() => setCarregando(false));
  }, [insumo.id]);

  if (carregando) return <p className="secao-sub">Carregando histórico...</p>;

  return (
    <DataTable
      densa
      linhas={historico}
      chaveDe={c => c.id}
      vazio={{ icone: 'nota', titulo: 'Nenhuma compra deste insumo', descricao: 'O histórico de preços aparece aqui depois da primeira compra.' }}
      colunas={[
        // No cartão: a data identifica a compra e o custo unitário é o número que
        // o produtor procura num histórico de preços.
        { chave: 'data', titulo: 'Data', papel: 'titulo', render: c => fmtData(c.dataCompra) },
        { chave: 'unit', titulo: 'Custo unit.', alinhar: 'direita', papel: 'destaque',
          render: c => fmtBRL(c.custoUnitario) },
        { chave: 'var', titulo: 'Variação', alinhar: 'direita', render: (c) => {
            const idx = historico.indexOf(c);
            const anterior = historico[idx + 1];
            if (!anterior || !anterior.custoUnitario) return <span className="texto-fraco">—</span>;
            const variacao = (c.custoUnitario - anterior.custoUnitario) / anterior.custoUnitario * 100;
            // Insumo mais caro que a compra anterior é notícia ruim: vermelho para cima.
            return (
              <span className={`variacao ${variacao > 0 ? 'variacao--alta' : 'variacao--baixa'}`}>
                <Icon nome={variacao > 0 ? 'chevron-cima' : 'chevron-baixo'} tamanho={13} />
                {Math.abs(variacao).toFixed(1)}%
              </span>
            );
          } },
        { chave: 'qtd', titulo: 'Quantidade', alinhar: 'direita', render: c => `${fmt(c.quantidade)} ${insumo.unidadeMedida || ''}` },
        { chave: 'total', titulo: 'Total', alinhar: 'direita', render: c => fmtBRL(c.quantidade * c.custoUnitario) },
        { chave: 'fornecedor', titulo: 'Fornecedor', ocultarSeVazia: true, valor: c => c.fornecedor, render: c => c.fornecedor || '' },
      ]}
    />
  );
});

// ── Card de Insumo no estoque ─────────────────────────────────────────────────
function InsumoCard({ item, ajusteId, setAjusteId, ajusteQtd, setAjusteQtd, salvarAjuste, salvandoAjuste, insumoExpandido, setInsumoExpandido, editInsumo, setEditInsumo, salvarEditInsumo, salvandoInsumo }) {
  const saldoNegativo = item.saldoTotal < 0;
  const editando = editInsumo?.insumoId === item.insumoId;
  const ajustando = ajusteId === item.insumoId;
  const expandido = insumoExpandido === item.insumoId;

  return (
    <div className={`insumo-card ${saldoNegativo ? 'insumo-card--negativo' : ''}`}>
      <div className="insumo-card__topo">
        <span className="insumo-card__nome">{item.nome}</span>
        <span className="tag">{item.categoria}</span>
        <span className="insumo-card__dado">
          Saldo <strong className={saldoNegativo ? 'valor-negativo' : ''}>{fmt(item.saldoTotal)} {item.unidadeMedida}</strong>
        </span>
        {item.custoMedioPonderado > 0 && (
          <span className="insumo-card__dado">
            Custo médio <strong>R$ {fmt(item.custoMedioPonderado)}/{item.unidadeMedida}</strong>
          </span>
        )}
      </div>

      <div className="insumo-card__acoes">
        <button type="button" className="btn-ghost btn-sm"
          onClick={e => { e.stopPropagation(); setAjusteId(null); setInsumoExpandido(null); setEditInsumo(editando ? null : { insumoId: item.insumoId, nome: item.nome, categoria: item.categoria }); }}>
          Editar
        </button>
        <button type="button" className="btn-ghost btn-sm"
          onClick={e => { e.stopPropagation(); setEditInsumo(null); setInsumoExpandido(null); setAjusteId(ajustando ? null : item.insumoId); setAjusteQtd(String(item.saldoTotal)); }}>
          Ajustar saldo
        </button>
        <button type="button" className="btn-ghost btn-sm insumo-card__historico"
          onClick={() => { setEditInsumo(null); setAjusteId(null); setInsumoExpandido(expandido ? null : item.insumoId); }}>
          {expandido ? 'Ocultar histórico' : 'Ver histórico de preços'}
        </button>
      </div>

      {editando && (
        <div className="insumo-card__painel">
          <Field label="Nome">
            <input value={editInsumo.nome} onChange={e => setEditInsumo(s => ({ ...s, nome: e.target.value }))} />
          </Field>
          <Field label="Categoria">
            <Select value={editInsumo.categoria} onChange={v => setEditInsumo(s => ({ ...s, categoria: v }))} options={CATEGORIAS} />
          </Field>
          <div className="insumo-card__painel-acoes">
            <button className="btn-secondary btn-sm" onClick={() => setEditInsumo(null)} disabled={salvandoInsumo}>Cancelar</button>
            <button className="btn-primary btn-sm" onClick={salvarEditInsumo} disabled={salvandoInsumo}>
              {salvandoInsumo ? 'Salvando...' : 'Salvar'}
            </button>
          </div>
        </div>
      )}

      {ajustando && (
        <div className="insumo-card__painel">
          <Field label="Novo saldo total" hint={item.unidadeMedida || undefined}>
            <input type="number" step="0.001" min="0" value={ajusteQtd}
              onChange={e => setAjusteQtd(e.target.value)} autoFocus disabled={salvandoAjuste} />
          </Field>
          <div className="insumo-card__painel-acoes">
            <button className="btn-secondary btn-sm" onClick={() => setAjusteId(null)} disabled={salvandoAjuste}>Cancelar</button>
            <button className="btn-primary btn-sm" onClick={() => salvarAjuste(item.insumoId)} disabled={salvandoAjuste}>
              {salvandoAjuste ? 'Ajustando...' : 'Confirmar'}
            </button>
          </div>
        </div>
      )}

      {expandido && (
        <div className="insumo-card__painel">
          <p className="rotulo-bloco">Histórico de preços</p>
          <HistoricoInsumo insumo={{ id: item.insumoId, unidadeMedida: item.unidadeMedida }} />
        </div>
      )}
    </div>
  );
}

// ── Página principal ──────────────────────────────────────────────────────────
export default function Insumos() {
  const [params, setParams] = useSearchParams();
  const [aba, setAba] = useState(params.get('aba') === 'estoque' ? 'estoque' : 'compras');

  const trocarAba = (novaAba) => {
    setAba(novaAba);
    if (params.has('aba')) setParams({}, { replace: true });
  };
  const [insumos, setInsumos] = useState([]);
  const [compras, setCompras] = useState([]);
  const [estoque, setEstoque] = useState([]);
  const [insumoExpandido, setInsumoExpandido] = useState(null);
  const [form, setForm] = useState(FORM_VAZIO);
  const [comprando, setComprando] = useState(false);

  const [ajusteId, setAjusteId] = useState(null);
  const [ajusteQtd, setAjusteQtd] = useState('');
  const [editInsumo, setEditInsumo] = useState(null); // { insumoId, nome, categoria }
  const [editCompra, setEditCompra] = useState(null); // compra em edição + consumo já apropriado
  // Um controle por ação: um booleano só travaria botões que não têm relação
  // com o que está sendo enviado.
  const [salvandoCompra, setSalvandoCompra] = useState(false);
  const [registrandoCompra, setRegistrandoCompra] = useState(false);
  const [salvandoAjuste, setSalvandoAjuste] = useState(false);
  const [salvandoInsumo, setSalvandoInsumo] = useState(false);
  const [excluindoId, setExcluindoId] = useState(null);
  const [usuarios, setUsuarios] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [erroCarga, setErroCarga] = useState('');
  const [filtroCategoria, setFiltroCategoria] = useState('');
  const { confirmar, ConfirmDialog } = useConfirm();

  // Antes as três chamadas engoliam o erro; numa queda de rede a tela mostrava
  // "Nenhuma compra registrada", o que faz o usuário pensar que perdeu dados.
  const carregar = useCallback(async () => {
    setCarregando(true);
    setErroCarga('');
    try {
      const [ins, cmp, est] = await Promise.all([
        api.getInsumos(), api.getCompras(), api.getEstoqueInsumos(),
      ]);
      setInsumos(ins || []);
      setCompras(cmp || []);
      setEstoque(est || []);
    } catch (err) {
      setErroCarga(err.message || 'Não foi possível carregar os dados de insumos.');
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => { carregar(); api.getUsuarios().then(setUsuarios).catch(() => setUsuarios([])); }, [carregar]);

  // Resultado de ação vai para o toast — canal único, some sozinho.
  const showMsg = useCallback((type, text) => {
    toast(text, type === 'error' ? 'error' : 'success');
  }, []);

  const categoriasComCompra = [...new Set(compras.map(c => c.insumo?.categoria).filter(Boolean))];
  const comprasFiltradas = filtroCategoria
    ? compras.filter(c => c.insumo?.categoria === filtroCategoria)
    : compras;

  const handleInsumo = (insumo) => {
    if (insumo?.id) {
      setForm(f => ({ ...f, insumoId: insumo.id, insumoNome: insumo.nome, unidadeMedida: insumo.unidadeMedida, isNovoInsumo: false }));
    } else {
      setForm(f => ({ ...f, insumoId: null, insumoNome: insumo?.nome || '', unidadeMedida: '', isNovoInsumo: true }));
    }
  };

  const handleCategoria = (cat) => {
    setForm(f => ({ ...FORM_VAZIO, dataCompra: f.dataCompra, categoria: cat }));
  };

  const registrarCompra = async (e) => {
    e.preventDefault();
    // Enter no formulário também submete, então o `disabled` do botão não basta
    // para evitar a compra duplicada.
    if (registrandoCompra) return;
    // Os campos de Select não travam o submit no navegador, então a checagem é aqui.
    if (!form.categoria) { showMsg('error', 'Escolha a categoria do insumo.'); return; }
    if (!form.insumoNome.trim()) { showMsg('error', 'Informe o nome do insumo.'); return; }
    if (form.isNovoInsumo && !form.unidadeMedida) { showMsg('error', 'Informe a unidade de medida.'); return; }
    setRegistrandoCompra(true);
    try {
      await api.registrarCompra({
        insumoId: form.insumoId || null,
        insumoNome: form.insumoId ? null : form.insumoNome,
        insumoCategoria: form.insumoId ? null : form.categoria,
        insumoUnidade: form.insumoId ? null : form.unidadeMedida,
        dataCompra: form.dataCompra,
        quantidade: parseFloat(form.quantidade),
        custoUnitario: parseFloat(form.custoUnitario),
        fornecedor: form.fornecedor || null,
        adquiridoEm: form.adquiridoEm || null,
      });
      setForm({ ...FORM_VAZIO, categoria: form.categoria });
      setComprando(false);
      showMsg('success', 'Compra registrada!');
      carregar();
    } catch (err) { showMsg('error', err.message); }
    finally { setRegistrandoCompra(false); }
  };

  const excluirCompra = useCallback(async (id) => {
    if (excluindoId) return;
    const ok = await confirmar({
      titulo: 'Excluir compra',
      mensagem: 'Esta compra será removida do histórico e o saldo do insumo será recalculado.',
      aviso: 'Esta ação não pode ser desfeita.',
    });
    if (!ok) return;
    setExcluindoId(id);
    try {
      await api.excluirCompra(id);
      showMsg('success', 'Compra excluída.');
      carregar();
    } catch (err) { showMsg('error', err.message); }
    finally { setExcluindoId(null); }
  }, [carregar, showMsg, confirmar, excluindoId]);

  const salvarAjuste = useCallback(async (insumoId) => {
    if (salvandoAjuste) return;
    if (ajusteQtd === '' || isNaN(parseFloat(ajusteQtd))) { showMsg('error', 'Informe um valor válido.'); return; }
    setSalvandoAjuste(true);
    try {
      await api.ajustarEstoque(insumoId, parseFloat(ajusteQtd));
      setAjusteId(null);
      setAjusteQtd('');
      showMsg('success', 'Estoque ajustado.');
      carregar();
    } catch (err) { showMsg('error', err.message); }
    finally { setSalvandoAjuste(false); }
  }, [ajusteQtd, carregar, showMsg, salvandoAjuste]);

  // Abre o modal já com o consumo calculado: é ele que limita a quantidade
  // mínima, então precisa estar visível antes de o usuário tentar salvar.
  const abrirEdicaoCompra = useCallback((c) => {
    setEditCompra({
      id: c.id,
      insumoNome: c.insumo?.nome || '',
      unidade: c.insumo?.unidadeMedida || '',
      consumido: Math.max(0, (c.quantidade || 0) - (c.quantidadeDisponivel || 0)),
      dataCompra: (c.dataCompra || '').slice(0, 10),
      quantidade: String(c.quantidade ?? ''),
      custoUnitario: String(c.custoUnitario ?? ''),
      fornecedor: c.fornecedor || '',
      adquiridoEm: c.adquiridoEm || '',
    });
  }, []);

  const salvarEdicaoCompra = useCallback(async () => {
    if (salvandoCompra) return;
    const c = editCompra;
    const qtd = parseFloat(c.quantidade);
    const custo = parseFloat(c.custoUnitario);

    if (!(qtd > 0)) { showMsg('error', 'Informe uma quantidade maior que zero.'); return; }
    if (!(custo >= 0)) { showMsg('error', 'Informe um custo unitário válido.'); return; }
    if (qtd < c.consumido) {
      showMsg('error', `Já foram usados ${fmt(c.consumido)} ${c.unidade}. A quantidade não pode ser menor.`);
      return;
    }

    setSalvandoCompra(true);
    try {
      await api.editarCompra(c.id, {
        dataCompra: c.dataCompra,
        quantidade: qtd,
        custoUnitario: custo,
        fornecedor: c.fornecedor || null,
        adquiridoEm: c.adquiridoEm || null,
      });
      setEditCompra(null);
      showMsg('success', 'Compra atualizada.');
      carregar();
    } catch (err) {
      showMsg('error', err.message);
    } finally {
      setSalvandoCompra(false);
    }
  }, [editCompra, carregar, showMsg, salvandoCompra]);

  const salvarEditInsumo = useCallback(async () => {
    if (!editInsumo || salvandoInsumo) return;
    setSalvandoInsumo(true);
    try {
      await api.editarInsumo(editInsumo.insumoId, { nome: editInsumo.nome, categoria: editInsumo.categoria });
      setEditInsumo(null);
      showMsg('success', 'Insumo atualizado.');
      carregar();
    } catch (err) { showMsg('error', err.message); }
    finally { setSalvandoInsumo(false); }
  }, [editInsumo, carregar, showMsg, salvandoInsumo]);

  const totalCompra = form.quantidade && form.custoUnitario
    ? parseFloat(form.quantidade) * parseFloat(form.custoUnitario)
    : null;

  const totalEdicao = editCompra && editCompra.quantidade && editCompra.custoUnitario
    ? parseFloat(editCompra.quantidade) * parseFloat(editCompra.custoUnitario)
    : null;

  return (
    <div>
      {ConfirmDialog}

      <PageHeader
        titulo="Insumos"
        icone="insumo"
        descricao="Compras, custo médio e saldo em estoque."
        acao={{ label: 'Registrar compra', icone: 'adicionar', onClick: () => setComprando(true) }}
      />

      {editCompra && (
        <Modal
          titulo="Editar compra"
          onClose={() => setEditCompra(null)}
          largura={560}
          // Fechar no meio do envio deixaria o usuário sem saber se salvou.
          bloqueado={salvandoCompra}
          rodape={<>
            <button className="btn-secondary" onClick={() => setEditCompra(null)} disabled={salvandoCompra}>
              Cancelar
            </button>
            <button className="btn-primary" onClick={salvarEdicaoCompra} disabled={salvandoCompra}>
              {salvandoCompra ? 'Salvando...' : 'Salvar alterações'}
            </button>
          </>}
        >
          <p className="secao-sub">
            Corrigindo a compra de <strong>{editCompra.insumoNome}</strong>.
            Para trocar o insumo, exclua esta compra e registre de novo.
          </p>

          {editCompra.consumido > 0 && (
            <div className="aviso aviso--atencao">
              <strong>{fmt(editCompra.consumido)} {editCompra.unidade}</strong> desta compra já
              foram aplicados em áreas de cultivo, então a quantidade não pode ficar abaixo disso.
              Mudar o custo unitário também não recalcula os custos já lançados — eles mantêm o
              valor do dia em que foram registrados.
            </div>
          )}

          <FormRow min={150}>
            <Field label="Data da compra" obrigatorio>
              <input type="date" value={editCompra.dataCompra}
                onChange={e => setEditCompra(c => ({ ...c, dataCompra: e.target.value }))} />
            </Field>
            <Field
              label={`Quantidade${editCompra.unidade ? ` (${editCompra.unidade})` : ''}`}
              obrigatorio
              hint={editCompra.consumido > 0 ? `Mínimo ${fmt(editCompra.consumido)}` : undefined}
            >
              <input type="number" step="0.01" min={editCompra.consumido || 0.01}
                value={editCompra.quantidade}
                onChange={e => setEditCompra(c => ({ ...c, quantidade: e.target.value }))} />
            </Field>
            <Field label="Custo unitário (R$)" obrigatorio>
              <input type="number" step="0.01" min="0"
                value={editCompra.custoUnitario}
                onChange={e => setEditCompra(c => ({ ...c, custoUnitario: e.target.value }))} />
            </Field>
          </FormRow>

          <FormRow min={170}>
            <Field label="Fornecedor" hint="Opcional">
              <input value={editCompra.fornecedor}
                onChange={e => setEditCompra(c => ({ ...c, fornecedor: e.target.value }))} />
            </Field>
            <Field label="Adquirido em nome de" hint="Opcional — ajuda a separar despesas por CPF">
              <Select
                value={editCompra.adquiridoEm}
                onChange={v => setEditCompra(c => ({ ...c, adquiridoEm: v }))}
                options={usuarios.map(u => ({ value: u.nome, label: u.nome }))}
                placeholder="Não informado"
              />
            </Field>
          </FormRow>

          {totalEdicao != null && (
            <div className="aviso aviso--ok">
              <strong>Total da compra: {fmtBRL(totalEdicao)}</strong>
            </div>
          )}
        </Modal>
      )}

      <div className="tabs" role="tablist">
        <button role="tab" aria-selected={aba === 'compras'} className={`tab ${aba === 'compras' ? 'active' : ''}`} onClick={() => trocarAba('compras')}>Compras</button>
        <button role="tab" aria-selected={aba === 'estoque'} className={`tab ${aba === 'estoque' ? 'active' : ''}`} onClick={() => trocarAba('estoque')}>Estoque</button>
      </div>

      {erroCarga && (
        <div className="aviso aviso--erro">
          {erroCarga}{' '}
          <button type="button" className="link-inline" onClick={carregar}>
            Tentar novamente
          </button>
        </div>
      )}

      {/* ── REGISTRAR COMPRA ── */}
      {comprando && (
        <Modal
          titulo="Registrar compra de insumo"
          onClose={() => setComprando(false)}
          largura={620}
          bloqueado={registrandoCompra}
        >
            <p className="secao-sub">
              O sistema calcula o custo médio e desconta do estoque quando você lançar
              o uso em uma área de cultivo.
            </p>
            <form onSubmit={registrarCompra}>
              <FormRow>
                <Field label="Categoria" obrigatorio largura="cheia">
                  <Select value={form.categoria} onChange={handleCategoria} options={CATEGORIAS} placeholder="Selecione a categoria" required />
                </Field>
              </FormRow>

              <FormRow>
                <Field
                  label="Insumo"
                  obrigatorio
                  largura="cheia"
                  hint={form.isNovoInsumo && form.insumoNome
                    ? 'Insumo novo: será criado no seu catálogo.'
                    : form.insumoId ? 'Insumo já cadastrado no seu catálogo.' : 'Digite para buscar ou criar.'}
                >
                  <InsumoAutocomplete
                    value={form.insumoId ? { id: form.insumoId, nome: form.insumoNome } : null}
                    onChange={handleInsumo}
                    categoria={form.categoria}
                    placeholder="Digite o nome do insumo..."
                  />
                </Field>
                <Field label="Unidade de medida" obrigatorio>
                  {form.isNovoInsumo
                    ? <Select value={form.unidadeMedida} onChange={v => setForm(f => ({ ...f, unidadeMedida: v }))} options={UNIDADES.map(u => ({ value: u, label: u }))} placeholder="Selecione" required />
                    : <input value={form.unidadeMedida || '—'} disabled />
                  }
                </Field>
              </FormRow>

              <FormRow>
                <Field label="Data da compra" obrigatorio>
                  <input type="date" value={form.dataCompra} onChange={e => setForm(f => ({ ...f, dataCompra: e.target.value }))} required />
                </Field>
                <Field label={`Quantidade${form.unidadeMedida ? ` (${form.unidadeMedida})` : ''}`} obrigatorio>
                  <input type="number" step="0.001" min="0.001" value={form.quantidade} onChange={e => setForm(f => ({ ...f, quantidade: e.target.value }))} required placeholder="Ex: 1000" />
                </Field>
                <Field label="Custo unitário (R$)" obrigatorio>
                  <input type="number" step="0.01" min="0.01" value={form.custoUnitario} onChange={e => setForm(f => ({ ...f, custoUnitario: e.target.value }))} required placeholder="Ex: 4,00" />
                </Field>
              </FormRow>

              {totalCompra != null && (
                <div className="aviso aviso--ok">
                  <strong>Total da compra:</strong> {fmtBRL(totalCompra)}
                  {form.unidadeMedida && (
                    <span>
                      {' '}({fmt(parseFloat(form.quantidade))} {form.unidadeMedida} × {fmtBRL(parseFloat(form.custoUnitario))})
                    </span>
                  )}
                </div>
              )}

              <FormRow>
                <Field label="Fornecedor" hint="Opcional">
                  <input value={form.fornecedor} onChange={e => setForm(f => ({ ...f, fornecedor: e.target.value }))} placeholder="Nome do fornecedor" />
                </Field>
                <Field label="Adquirido em nome de" hint="Ajuda a separar despesas por CPF no imposto de renda">
                  <Select value={form.adquiridoEm} onChange={v => setForm(f => ({ ...f, adquiridoEm: v }))} options={usuarios.map(u => ({ value: u.nome, label: u.nome }))} placeholder="Opcional" />
                </Field>
              </FormRow>

              <div className="actions modal-rodape">
                <button type="button" className="btn-secondary" onClick={() => setComprando(false)}
                  disabled={registrandoCompra}>
                  Cancelar
                </button>
                <button type="submit" className="btn-primary"
                  disabled={registrandoCompra || !form.categoria || !form.insumoNome.trim()}>
                  {registrandoCompra ? 'Registrando...' : 'Registrar compra'}
                </button>
              </div>
            </form>
        </Modal>
      )}

      {aba === 'compras' && (
          <div className="card">
            <div className="secao-titulo">
              <h2>Compras registradas</h2>
              {compras.length > 0 && <span className="contador">{comprasFiltradas.length} de {compras.length}</span>}
            </div>

            {/* Filtros: a lista cresce rápido e ficava sem nenhuma forma de recorte. */}
            {compras.length > 0 && (
              <div className="chips chips--filtro">
                <span className="chips-label">Categoria:</span>
                <button className={`chip ${!filtroCategoria ? 'chip--ativo' : ''}`}
                  onClick={() => setFiltroCategoria('')}>Todas</button>
                {categoriasComCompra.map(cat => (
                  <button key={cat} className={`chip ${filtroCategoria === cat ? 'chip--ativo' : ''}`}
                    onClick={() => setFiltroCategoria(cat)}>
                    {CATEGORIAS.find(c => c.value === cat)?.label || cat}
                  </button>
                ))}
              </div>
            )}

            <DataTable
              densa
              linhas={comprasFiltradas}
              chaveDe={c => c.id}
              vazio={{
                icone: 'insumo',
                titulo: compras.length === 0 ? 'Nenhuma compra registrada' : 'Nada nesta categoria',
                descricao: compras.length === 0
                  ? 'Registre a primeira compra para começar a controlar seu estoque.'
                  : 'Escolha outra categoria para ver as compras.',
                acao: compras.length === 0
                  ? { label: 'Registrar compra', onClick: () => setComprando(true) }
                  : undefined,
              }}
              colunas={[
                // No cartão: o insumo identifica a compra, o total é o destaque, e
                // a data vira a linha de contexto. Sem papéis, cada compra ocupava
                // nove linhas iguais no celular.
                { chave: 'data', titulo: 'Data', papel: 'subtitulo', render: c => fmtData(c.dataCompra) },
                { chave: 'insumo', titulo: 'Insumo', papel: 'titulo', render: c => (
                    <span>
                      <strong>{c.insumo?.nome}</strong>
                      <span className="celula-sub">{c.insumo?.categoria}</span>
                    </span>
                  ) },
                { chave: 'qtd', titulo: 'Qtd', alinhar: 'direita', render: c => `${fmt(c.quantidade)} ${c.insumo?.unidadeMedida || ''}` },
                { chave: 'unit', titulo: 'Custo unit.', alinhar: 'direita', render: c => fmtBRL(c.custoUnitario) },
                { chave: 'total', titulo: 'Total', alinhar: 'direita', papel: 'destaque',
                  render: c => fmtBRL(c.quantidade * c.custoUnitario) },
                { chave: 'saldo', titulo: 'Saldo', alinhar: 'direita', render: c => (
                    <span className={c.quantidadeDisponivel > 0 ? undefined : 'texto-fraco'}>
                      {fmt(c.quantidadeDisponivel)} {c.insumo?.unidadeMedida || ''}
                    </span>
                  ) },
                // Vazio em vez de "—": no cartão do celular a linha some em vez de
                // virar "FORNECEDOR —".
                { chave: 'fornecedor', titulo: 'Fornecedor', ocultarSeVazia: true, valor: c => c.fornecedor, render: c => c.fornecedor || '' },
                { chave: 'adquirente', titulo: 'Adquirente', ocultarSeVazia: true, valor: c => c.adquiridoEm, render: c => c.adquiridoEm || '' },
                { chave: 'acoes', titulo: 'Ações', render: c => (
                    <span className="celula-acoes">
                      {/* Ajuste manual de estoque não é compra: quantidade e saldo
                          não guardam a relação comprado − consumido. */}
                      {c.fornecedor === 'AJUSTE_MANUAL' ? (
                        <span className="texto-fraco">Ajuste</span>
                      ) : (
                        <button type="button" className="item-card-btn"
                          onClick={() => abrirEdicaoCompra(c)}>
                          Editar
                        </button>
                      )}
                      <button type="button" className="item-card-btn item-card-btn--danger"
                        onClick={() => excluirCompra(c.id)} disabled={!!excluindoId}>
                        {excluindoId === c.id ? 'Excluindo...' : 'Excluir'}
                      </button>
                    </span>
                  ) },
              ]}
            />
          </div>
      )}

      {/* ── ESTOQUE + CATÁLOGO UNIFICADOS ── */}
      {aba === 'estoque' && (
        <div className="card">
          <div className="secao-titulo">
            <h2>Estoque de insumos</h2>
            {estoque.length > 0 && <span className="contador">{estoque.length}</span>}
          </div>
          <p className="secao-sub">
            Saldo disponível após descontar todos os consumos. Clique em um insumo para ver o histórico de preços.
          </p>
          {estoque.length === 0 ? (
            <EmptyState
              compacto
              icone="estoque"
              titulo="Nenhum insumo em estoque"
              descricao="O saldo aparece aqui assim que a primeira compra for registrada."
              acao={{ label: 'Registrar compra', onClick: () => { trocarAba('compras'); setComprando(true); } }}
            />
          ) : (() => {
            const comEstoque = estoque.filter(i => i.saldoTotal > 0);
            const semEstoque = estoque.filter(i => i.saldoTotal <= 0);
            const propsCard = {
              ajusteId, setAjusteId, ajusteQtd, setAjusteQtd, salvarAjuste, salvandoAjuste,
              insumoExpandido, setInsumoExpandido, editInsumo, setEditInsumo,
              salvarEditInsumo, salvandoInsumo,
            };
            return (
              <>
                {comEstoque.map(item => (
                  <InsumoCard key={item.insumoId} item={item} {...propsCard} />
                ))}
                <div className="estoque-total">
                  Total em estoque
                  <strong>R$ {fmt(comEstoque.reduce((acc, i) => acc + i.valorTotalEstoque, 0))}</strong>
                </div>
                {semEstoque.length > 0 && (
                  <>
                    <h3 className="subsecao">Insumos já registrados e sem estoque</h3>
                    {semEstoque.map(item => (
                      <InsumoCard key={item.insumoId} item={item} {...propsCard} />
                    ))}
                  </>
                )}
              </>
            );
          })()}
        </div>
      )}
    </div>
  );
}
